import React, { useEffect, useState } from "react";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";

import Paper from "@mui/material/Paper";
import Draggable from "react-draggable";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";

import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMove";
import DescriptionIcon from "@mui/icons-material/Description";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

function PaperComponent(props) {
  return (
    <Draggable handle="#draggable-eq-ficheiros-title" cancel={'[class*="MuiDialogContent-root"]'}>
      <Paper {...props} />
    </Draggable>
  );
}

export default function DialogEquipamentoFicheiros({
  open,
  onClose,
  idEquipamento,
  nomeEquipamento = "",
  acesso = "Menu Equipamentos",
  subpastas = ["Documentos", "Imagens", "Outros", "Eventos", "Confidencial"],
  mostrarSelectorSubpasta = true,
  idevento = 0,
  titulo = "",
}) {
  const { showAlert } = useAlert();
  const { temPermissao, userData, temFuncao } = useAuth();
  const idutilizador = userData?.id || 0;

  const [loading, setLoading] = useState(false);

  const [subpasta, setSubpasta] = useState(subpastas?.[0] || "Documentos");
  const [ficheiros, setFicheiros] = useState([]);

  const podeLer = temPermissao(acesso, "r");
  const podeCriar = temPermissao(acesso, "w");
  const podeEditar = temPermissao(acesso, "u");
  const podeApagar = temPermissao(acesso, "d");

  const isAdminLike =
    temFuncao?.("Administração") || temFuncao?.("Informática") || temFuncao?.("Tesouraria");

  // ----------------- Dialog MOVER -----------------
  const [openMove, setOpenMove] = useState(false);
  const [moveNome, setMoveNome] = useState("");
  const [moveDestino, setMoveDestino] = useState(subpastas?.[0] || "Documentos");

  // ----------------- Dialog RENOMEAR -----------------
  const [openRename, setOpenRename] = useState(false);
  const [renameNome, setRenameNome] = useState("");
  const [renameNovo, setRenameNovo] = useState("");
  const [renameExt, setRenameExt] = useState("");

  const [canCamera, setCanCamera] = useState(false);

  useEffect(() => {
    try {
      const ua = String(navigator.userAgent || "");
      const isMobile = /iPhone|iPad|iPod|Android/i.test(ua);
      setCanCamera(isMobile);
    } catch {
      setCanCamera(false);
    }
  }, []);

  const inputGhostStyle = {
    position: "absolute",
    left: "-9999px",
    width: 1,
    height: 1,
    opacity: 0,
    pointerEvents: "none",
  };

  const fechar = () => {
    setFicheiros([]);
    setSubpasta(subpastas?.[0] || "Documentos");

    setOpenMove(false);
    setMoveNome("");
    setMoveDestino(subpastas?.[0] || "Documentos");

    setOpenRename(false);
    setRenameNome("");
    setRenameNovo("");

    onClose?.();
  };

  const isImagem = (nome = "") => {
    const n = String(nome).toLowerCase();
    return (
      n.endsWith(".jpg") ||
      n.endsWith(".jpeg") ||
      n.endsWith(".png") ||
      n.endsWith(".webp") ||
      n.endsWith(".gif")
    );
  };

  const subpastasVisiveis = (subpastas || []).filter((s) => {
    if (String(s).toLowerCase() === "confidencial" && !isAdminLike) return false;
    return true;
  });

  useEffect(() => {
    if (!isAdminLike && subpasta === "Confidencial") {
      setSubpasta(subpastasVisiveis[0] || "Documentos");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminLike, subpasta]);

  useEffect(() => {
    if (!mostrarSelectorSubpasta) {
      setSubpasta(subpastas?.[0] || "Documentos");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mostrarSelectorSubpasta, open, idEquipamento, idevento]);

  const carregar = async () => {
    if (!open) return;
    if (!idEquipamento) return;

    if (!podeLer) {
      showAlert("Sem permissões para consultar ficheiros.", "error");
      return;
    }

    setLoading(true);
    try {
      const [data, err, meta] = await handleApi(
        api.post("EquipamentosFicheiros/Sel", {
          idequipamento: idEquipamento,
          subpasta,
          idutilizador,
          idevento: Number(idevento || 0),
        })
      );

      if (err) {
        showAlert(err, "error");
        setFicheiros([]);
        return;
      }

      setFicheiros(Array.isArray(data) ? data : []);
      if (meta?.message) showAlert(meta.message, meta.severity || "success");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    (async () => {
      await carregar();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, subpasta, idEquipamento]);

  const pedirApagar = async (nome) => {
    if (!podeApagar) return showAlert("Sem permissões para eliminar.", "error");
    if (!nome) return;

    setLoading(true);
    try {
      const [_, err, meta] = await handleApi(
        api.post("EquipamentosFicheiros/Del", {
          idequipamento: idEquipamento,
          subpasta,
          nome,
          idutilizador,
        })
      );

      if (err) return showAlert(err, "error");
      if (meta?.message) showAlert(meta.message, meta.severity || "success");

      await carregar();
    } finally {
      setLoading(false);
    }
  };

  const abrirFicheiro = (url) => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const compressImageIfNeeded = async (file) => {
    try {
      if (!file) return file;

      const type = String(file.type || "").toLowerCase();
      const name = String(file.name || "").toLowerCase();

      const isImg =
        type.startsWith("image/") ||
        name.endsWith(".jpg") ||
        name.endsWith(".jpeg") ||
        name.endsWith(".png") ||
        name.endsWith(".webp");

      // Só comprimimos imagens
      if (!isImg) return file;

      // Se já é pequeno, não mexer (evita trabalho)
      if (file.size <= 350 * 1024) return file; // <= 350 KB

      const MAX_DIM = 1600; // bom compromisso p/ documentos e fotos
      const QUALITY = 0.75; // 0.70~0.80 costuma ficar excelente

      // --- decode ---
      let bitmap;
      if ("createImageBitmap" in window) {
        bitmap = await createImageBitmap(file);
      } else {
        // fallback
        bitmap = await new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = URL.createObjectURL(file);
        });
      }

      const w = bitmap.width;
      const h = bitmap.height;

      let nw = w;
      let nh = h;

      if (w >= h && w > MAX_DIM) {
        nw = MAX_DIM;
        nh = Math.round((h * MAX_DIM) / w);
      } else if (h > w && h > MAX_DIM) {
        nh = MAX_DIM;
        nw = Math.round((w * MAX_DIM) / h);
      }

      const canvas = document.createElement("canvas");
      canvas.width = nw;
      canvas.height = nh;

      const ctx = canvas.getContext("2d", { alpha: false });
      ctx.drawImage(bitmap, 0, 0, nw, nh);

      // converte para JPEG (mais leve e suficiente para o vosso caso)
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", QUALITY));
      if (!blob) return file;

      const base = file.name?.replace(/\.[^/.]+$/, "") || "imagem";
      const newName = `${base}.jpg`;

      const compressed = new File([blob], newName, {
        type: "image/jpeg",
        lastModified: Date.now(),
      });

      // se por algum motivo ficou maior, fica o original
      if (compressed.size >= file.size) return file;

      return compressed;
    } catch {
      return file; // fallback seguro
    }
  };

  // Upload (ficheiro)
  const onUploadFiles = async (files) => {
    if (!podeCriar) {
      showAlert("Sem permissões para carregar ficheiros.", "error");
      return;
    }
    if (!idEquipamento) {
      showAlert("Equipamento inválido.", "error");
      return;
    }
    if (!Array.isArray(files) || files.length === 0) return;

    setLoading(true);

    let ok = 0;
    let falhas = 0;
    let primeiraFalha = null;

    try {
      for (const file of files) {
        try {
          const fileFinal = await compressImageIfNeeded(file);

          const fd = new FormData();
          fd.append("idequipamento", String(idEquipamento));
          fd.append("subpasta", subpasta);
          fd.append("idutilizador", String(idutilizador));
          fd.append("idevento", String(Number(idevento || 0)));
          fd.append("ficheiro", fileFinal);

          const [_, err] = await handleApi(
            api.post("EquipamentosFicheiros/Upload", fd, {
              headers: { "Content-Type": "multipart/form-data" },
            })
          );

          if (err) {
            falhas++;
            if (!primeiraFalha) primeiraFalha = err;
            continue;
          }

          ok++;
        } catch (e) {
          falhas++;
          if (!primeiraFalha) primeiraFalha = "Erro ao carregar ficheiro.";
        }
      }

      if (ok > 0) showAlert(`Upload concluído: ${ok} ficheiro(s).`, "success");
      if (falhas > 0)
        showAlert(`Falharam ${falhas} ficheiro(s). ${primeiraFalha || ""}`, "warning");

      await carregar();
    } finally {
      setLoading(false);
    }
  };

  // ----------------- MOVER (Dialog) -----------------
  const abrirMover = (nome) => {
    if (!podeEditar) return showAlert("Sem permissões para mover.", "error");
    if (!nome) return;

    setMoveNome(nome);

    const sugestao =
      (subpastasVisiveis || []).find((s) => s !== subpasta) ||
      (subpastas || []).find((s) => s !== subpasta) ||
      subpasta;

    setMoveDestino(sugestao);
    setOpenMove(true);
  };

  const confirmarMover = async () => {
    if (!podeEditar) return showAlert("Sem permissões para mover.", "error");
    if (!moveNome) return;

    if (!moveDestino || moveDestino === subpasta) {
      return showAlert("Escolhe uma pasta diferente.", "warning");
    }

    setLoading(true);
    try {
      const [_, err, meta] = await handleApi(
        api.post("EquipamentosFicheiros/Move", {
          idequipamento: idEquipamento,
          origem: subpasta,
          destino: moveDestino,
          nomes: [moveNome],
          idutilizador,
        })
      );

      if (err) return showAlert(err, "error");
      if (meta?.message) showAlert(meta.message, meta.severity || "success");

      setOpenMove(false);
      setMoveNome("");
      await carregar();
    } finally {
      setLoading(false);
    }
  };

  // ----------------- RENOMEAR (Dialog) -----------------
  const splitNomeExt = (nome = "") => {
    const idx = nome.lastIndexOf(".");
    if (idx <= 0) {
      return { base: nome, ext: "" };
    }
    return {
      base: nome.substring(0, idx),
      ext: nome.substring(idx), // inclui o ponto
    };
  };

  const abrirRenomear = (nome) => {
    if (!podeEditar) return showAlert("Sem permissões para renomear.", "error");
    if (!nome) return;

    const { base, ext } = splitNomeExt(nome);

    setRenameNome(nome); // nome completo original
    setRenameNovo(base); // só o nome base (sem extensão)
    setRenameExt(ext); // extensão fixa
    setOpenRename(true);
  };

  const isNomeSeguro = (s) => {
    const t = String(s || "").trim();
    if (!t) return false;
    // bloqueia paths / traversal
    if (t.includes("/") || t.includes("\\") || t.includes("..")) return false;
    return true;
  };

  const confirmarRenomear = async () => {
    if (!podeEditar) return showAlert("Sem permissões para renomear.", "error");
    if (!renameNome) return;

    const novoBase = String(renameNovo || "").trim();

    if (!isNomeSeguro(novoBase)) {
      return showAlert("Nome inválido. Não use / \\ ou ..", "warning");
    }

    const novoNome = novoBase + renameExt;

    if (!isNomeSeguro(novoNome)) {
      return showAlert("Nome inválido. Não use / \\ ou ..", "warning");
    }

    if (novoNome === renameNome) {
      setOpenRename(false);
      setRenameNome("");
      setRenameNovo("");
      return;
    }

    setLoading(true);
    try {
      const [_, err, meta] = await handleApi(
        api.post("EquipamentosFicheiros/Rename", {
          idequipamento: idEquipamento,
          subpasta,
          nome: renameNome,
          novo_nome: novoNome,
          idutilizador,
        })
      );

      if (err) return showAlert(err, "error");
      if (meta?.message) showAlert(meta.message, meta.severity || "success");

      setOpenRename(false);
      setRenameNome("");
      setRenameNovo("");
      await carregar();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={fechar} maxWidth="md" fullWidth PaperComponent={PaperComponent}>
      <DialogTitle id="draggable-eq-ficheiros-title" sx={{ cursor: "move" }}>
        {titulo ||
          `Ficheiros do Equipamento ${nomeEquipamento ? nomeEquipamento : "—"}${
            idEquipamento ? ` (ID: ${idEquipamento})` : ""
          }`}
      </DialogTitle>

      {loading && (
        <MDBox py={2} display="flex" alignItems="center" justifyContent="center">
          <CircularProgress />
        </MDBox>
      )}

      <DialogContent>
        {/* Linha 1: pasta + ações gerais */}
        <MDBox display="flex" gap={2} alignItems="flex-end" flexWrap="wrap" mt={0.5}>
          <div style={{ minWidth: 220 }}>
            <div style={{ fontSize: 12, color: "#7b809a", marginBottom: 4 }}>Pasta</div>

            {mostrarSelectorSubpasta ? (
              <select
                value={subpasta}
                onChange={(e) => {
                  setSubpasta(e.target.value);
                }}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  borderRadius: 8,
                  fontSize: 13,
                  border: "1px solid rgba(0,0,0,0.15)",
                  outline: "none",
                }}
                disabled={!podeLer}
              >
                {(subpastasVisiveis || []).map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            ) : (
              <div
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  borderRadius: 8,
                  fontSize: 13,
                  border: "1px solid rgba(0,0,0,0.15)",
                  background: "#f5f5f5",
                  color: "#344767",
                  minHeight: 40,
                  display: "flex",
                  alignItems: "center",
                  boxSizing: "border-box",
                }}
              >
                {subpasta}
              </div>
            )}
          </div>

          <MDBox display="flex" gap={1} alignItems="center" mb={0.25}>
            <input
              id="eq-ficheiros-upload"
              type="file"
              multiple
              style={{ display: "none" }}
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                onUploadFiles(files);
                e.target.value = "";
              }}
              disabled={!podeCriar}
            />

            <MDButton
              color="success"
              disabled={!podeCriar || loading}
              startIcon={<CloudUploadIcon />}
              onClick={() => document.getElementById("eq-ficheiros-upload")?.click?.()}
            >
              Upload
            </MDButton>

            {canCamera && (
              <MDButton
                color="info"
                disabled={!podeCriar || loading}
                startIcon={<PhotoCameraIcon />}
                component="label"
              >
                Câmera
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  style={inputGhostStyle}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = "";
                    if (f) onUploadFiles([f]); // reaproveitas o teu upload multipart tal como está
                  }}
                />
              </MDButton>
            )}
          </MDBox>
        </MDBox>

        {/* Lista */}
        <MDBox mt={2}>
          <MDTypography variant="button" color="text" fontWeight="regular" sx={{ fontSize: 12 }}>
            Ficheiros ({ficheiros.length})
          </MDTypography>

          <MDBox mt={1} sx={{ borderRadius: 1, overflow: "auto", border: "1px solid #eee" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, width: 120 }}>Ações</th>
                  <th style={thStyle}>Nome</th>
                  <th style={{ ...thStyle, width: 72 }}>Miniat.</th>

                  <th style={thStyle}>Tamanho</th>
                  <th style={thStyle}>Atualizado</th>
                </tr>
              </thead>

              <tbody>
                {ficheiros.map((f) => {
                  const nome = f?.nome || "";
                  const url = f?.url || "";
                  const imagem = isImagem(nome);

                  return (
                    <tr key={nome}>
                      <td style={{ ...tdStyle, width: 120, whiteSpace: "nowrap" }}>
                        <Tooltip title="Renomear">
                          <span>
                            <IconButton
                              size="small"
                              color="warning"
                              disabled={!podeEditar || !nome}
                              onClick={() => abrirRenomear(nome)}
                            >
                              <DriveFileRenameOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>

                        <Tooltip title="Mover">
                          <span>
                            <IconButton
                              size="small"
                              color="info"
                              disabled={!podeEditar || !nome}
                              onClick={() => abrirMover(nome)}
                            >
                              <DriveFileMoveIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>

                        <Tooltip title="Eliminar">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={!podeApagar || !nome}
                              onClick={() => pedirApagar(nome)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </td>

                      <td style={tdStyle}>
                        {url ? (
                          <span
                            style={{ cursor: "pointer", textDecoration: "underline" }}
                            title="Abrir"
                            onClick={() => abrirFicheiro(url)}
                          >
                            {nome || "—"}
                          </span>
                        ) : (
                          nome || "—"
                        )}
                      </td>

                      <td style={{ ...tdStyle, width: 72 }}>
                        {imagem && url ? (
                          <img
                            src={url}
                            loading="lazy"
                            decoding="async"
                            alt={nome}
                            onClick={() => abrirFicheiro(url)}
                            title="Abrir"
                            style={{
                              width: 46,
                              height: 46,
                              objectFit: "cover",
                              borderRadius: 6,
                              cursor: "pointer",
                              border: "1px solid rgba(0,0,0,0.12)",
                            }}
                          />
                        ) : (
                          <Tooltip title="Abrir">
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                disabled={!url}
                                onClick={() => abrirFicheiro(url)}
                              >
                                <DescriptionIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </td>

                      <td style={tdStyle}>{f?.tamanho ?? "—"}</td>
                      <td style={tdStyle}>{f?.updatedat ?? "—"}</td>
                    </tr>
                  );
                })}

                {ficheiros.length === 0 && (
                  <tr>
                    <td style={tdStyle} colSpan={5}>
                      Sem ficheiros nesta pasta.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </MDBox>
        </MDBox>
      </DialogContent>

      <DialogActions>
        {/* <MDButton onClick={carregar} color="info" disabled={!podeLer || loading}>
          Atualizar
        </MDButton> */}
        <MDButton onClick={fechar} color="secondary">
          Fechar
        </MDButton>
      </DialogActions>

      {/* --------------------- DIALOG MOVER --------------------- */}
      <Dialog open={openMove} onClose={() => setOpenMove(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Mover ficheiro</DialogTitle>
        <DialogContent>
          <MDBox mt={1} display="grid" gap={1.25}>
            <MDTypography variant="button" sx={{ fontSize: 12 }}>
              Ficheiro: <b>{moveNome || "—"}</b>
            </MDTypography>

            <div>
              <div style={{ fontSize: 12, color: "#7b809a", marginBottom: 4 }}>Destino</div>
              <select
                value={moveDestino}
                onChange={(e) => setMoveDestino(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 8,
                  fontSize: 13,
                  border: "1px solid rgba(0,0,0,0.15)",
                  outline: "none",
                }}
              >
                {(subpastasVisiveis || subpastas || []).map((s) => (
                  <option key={s} value={s} disabled={s === subpasta}>
                    {s === subpasta ? `${s} (atual)` : s}
                  </option>
                ))}
              </select>
            </div>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setOpenMove(false)} color="secondary" disabled={loading}>
            Cancelar
          </MDButton>
          <MDButton onClick={confirmarMover} color="success" disabled={!podeEditar || loading}>
            Confirmar
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* --------------------- DIALOG RENOMEAR --------------------- */}
      <Dialog open={openRename} onClose={() => setOpenRename(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Renomear ficheiro</DialogTitle>
        <DialogContent>
          <MDBox mt={1} display="grid" gap={1.25}>
            <MDTypography variant="button" sx={{ fontSize: 12 }}>
              Atual: <b>{renameNome || "—"}</b>
            </MDTypography>

            <div>
              <div style={{ fontSize: 12, color: "#7b809a", marginBottom: 4 }}>Novo nome</div>
              <TextField
                value={renameNovo}
                onChange={(e) => setRenameNovo(e.target.value)}
                placeholder="ex: Documento_20260131 (nome_aaaammdd)"
                size="small"
                fullWidth
                disabled={loading}
              />
              <MDTypography variant="caption" sx={{ display: "block", mt: 0.5, color: "#7b809a" }}>
                Extensão: <b>{renameExt || "—"}</b>
              </MDTypography>
            </div>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenRename(false);
              setRenameNome("");
              setRenameNovo("");
            }}
            color="secondary"
            disabled={loading}
          >
            Cancelar
          </MDButton>
          <MDButton onClick={confirmarRenomear} color="success" disabled={!podeEditar || loading}>
            Confirmar
          </MDButton>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

const thStyle = {
  position: "sticky",
  top: 0,
  zIndex: 2,
  background: "#fff",
  textAlign: "left",
  padding: "8px 10px",
  fontSize: 12,
  color: "#7b809a",
  boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
};

const tdStyle = {
  padding: "8px 10px",
  borderTop: "1px solid #eee",
  fontSize: 12,
  color: "#1a2b4c",
};
